import React, { useEffect, useState } from 'react';
import Navbar from 'components/UI/Navbar/Navbar';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  Stack,
  InputAdornment,
  Pagination,
  TextField,
  Menu,
  MenuItem,
  Button,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { couponApi } from '../../service/CouponService';

interface Coupon {
  id: number;
  code: string;
  type: 'FIXED' | 'PERCENT' | 'ZERO_FEE';
  amount: number;
  startsAt: string;
  endsAt: string;
  status: 'ACTIVE' | 'INACTIVE' | 'SUBMITTED' | 'EXPIRED';
  channel?: 'IOS' | 'ANDROID' | 'APP' | 'WEB' | 'ALL';
  totalLimit?: number | null;
  perUserLimit?: number | null;
}

export default function CouponList() {
  const navigate = useNavigate();

  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCoupon, setSelectedCoupon] = useState<Coupon | null>(null);
  const open = Boolean(anchorEl);

  // Utility: Format Dates
  const formatDate = (iso: string) => {
    return new Date(iso).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  // Get days left until expiry
  const getDaysLeft = (endIso: string) => {
    const now = new Date();
    const end = new Date(endIso);
    const diff = end.getTime() - now.getTime();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  // Badge styling
  const badgeStyle = {
    fontSize: '12px',
    background: '#f5f5f5',
    padding: '2px 6px',
    borderRadius: '6px',
    display: 'inline-block',
    marginRight: '6px',
  };

  const fetchCoupons = async () => {
    const list = await couponApi.getAll();
    setCoupons(list);
  };

  useEffect(() => {
    fetchCoupons();
  }, []);

  // Reset pagination if search changes
  useEffect(() => {
    setPage(1);
  }, [search]);

  const handleMenuClick = (e: any, coupon: Coupon) => {
    setAnchorEl(e.currentTarget);
    setSelectedCoupon(coupon);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedCoupon(null);
  };

  const handleStatusUpdate = async (status: 'ACTIVE' | 'INACTIVE') => {
    if (!selectedCoupon) return;
    await couponApi.updateStatus({ id: selectedCoupon.id, status });
    await fetchCoupons();
    handleCloseMenu();
  };

  // Search filter
  const filteredCoupons = coupons.filter((c) => {
    const term = search.toLowerCase();
    return (
      c.code.toLowerCase().includes(term) ||
      c.type.toLowerCase().includes(term) ||
      c.status.toLowerCase().includes(term) ||
      (c.channel ?? 'ALL').toLowerCase().includes(term)
    );
  });

  // Pagination
  const paginated = filteredCoupons.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage,
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'SUBMITTED':
        return 'warning';
      case 'INACTIVE':
        return 'default';
      case 'EXPIRED':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <>
    <Navbar />
    <Box p={4}>
      {/* HEADER WITH CREATE BUTTON */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" fontWeight={700}>
          Coupons
        </Typography>

        <Button
          variant="contained"
          onClick={() => navigate('/host/coupons/new')}
        >
          Create Coupon
        </Button>
      </Box>

      {/* SEARCH BAR */}
      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent>
          <TextField
            fullWidth
            placeholder="Search coupons..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </CardContent>
      </Card>

      {/* COUPON LIST */}
      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          {paginated.map((coupon) => {
            const daysLeft = getDaysLeft(coupon.endsAt);

            return (
              <Stack
                key={coupon.id}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                p={2}
                borderBottom="1px solid #eee"
              >
                <Box>
                  {/* CODE */}
                  <Typography fontWeight={600}>{coupon.code}</Typography>

                  {/* TYPE + AMOUNT */}
                  <Typography variant="body2" color="text.secondary">
                    {coupon.type} • {coupon.amount}
                  </Typography>

                  {/* DATE BADGES */}
                  <Stack direction="row" spacing={1} mt={0.5}>
                    <Box sx={badgeStyle}>
                      Starts: {formatDate(coupon.startsAt)}
                    </Box>

                    <Box sx={badgeStyle}>Ends: {formatDate(coupon.endsAt)}</Box>

                    <Box sx={badgeStyle}>
                      Channel: {coupon.channel ?? 'ALL'}
                    </Box>
                  </Stack>

                  {/* EXPIRY STATUS */}
                  {daysLeft < 0 ? (
                    <Typography variant="body2" color="error" fontWeight={600}>
                      Expired
                    </Typography>
                  ) : daysLeft <= 7 ? (
                    <Typography
                      variant="body2"
                      color="warning.main"
                      fontWeight={600}
                    >
                      Ends in {daysLeft} day{daysLeft !== 1 ? 's' : ''}
                    </Typography>
                  ) : (
                    <Typography
                      variant="body2"
                      color="success.main"
                      fontWeight={600}
                    >
                      {daysLeft} days left
                    </Typography>
                  )}
                </Box>

                {/* STATUS CHIP */}
                <Chip
                  label={coupon.status}
                  color={getStatusColor(coupon.status)}
                />

                {/* ACTION MENU */}
                <IconButton onClick={(e) => handleMenuClick(e, coupon)}>
                  <MoreVertIcon />
                </IconButton>
              </Stack>
            );
          })}

          {/* PAGINATION */}
          <Pagination
            sx={{ mt: 3 }}
            count={Math.ceil(filteredCoupons.length / rowsPerPage)}
            page={page}
            onChange={(_, v) => setPage(v)}
          />
        </CardContent>
      </Card>

      {/* MENU */}
      <Menu anchorEl={anchorEl} open={open} onClose={handleCloseMenu}>
        {selectedCoupon?.status === 'ACTIVE' && (
          <MenuItem onClick={() => handleStatusUpdate('INACTIVE')}>
            Deactivate
          </MenuItem>
        )}

        {selectedCoupon?.status !== 'ACTIVE' && (
          <MenuItem
            onClick={() => navigate(`/host/coupons/${selectedCoupon?.id}/edit`)}
          >
            Edit
          </MenuItem>
        )}

        {(selectedCoupon?.status === 'INACTIVE' ||
          selectedCoupon?.status === 'SUBMITTED') && (
          <MenuItem onClick={() => handleStatusUpdate('ACTIVE')}>
            Activate
          </MenuItem>
        )}
      </Menu>
    </Box>
    </>
  );
}
